import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Printer, Ban, Warehouse, ArrowLeftRight } from "lucide-react";
import useDepositos from "../hooks/useDepositos";
import TransferenciaDepositoModal from "./TransferenciaDepositoModal";
import { imprimirRemitoDeposito } from "./RemitoDeposito";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-cyan-500/50";
const fmt = (n) => Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 3 });

const MODOS = [
  { v: "sin_depositos", t: "Sin depósitos", d: "El inventario funciona como siempre, con un único stock por producto." },
  { v: "principal", t: "Un depósito principal", d: "Todo ingresa al depósito principal. Se pueden crear otros depósitos y transferir entre ellos." },
  { v: "por_compra", t: "Elegir depósito en cada compra", d: "Cada compra se asigna a un depósito predeterminado, con opción de elegir otro." },
];

export default function DepositosPanel({ token, logo, puedeGestionar, puedeTransferir, puedeAnular }) {
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const { cfg, depositos, cargando, recargar } = useDepositos(token, logo);
  const [todos, setTodos] = useState([]);        // incluye inactivos
  const [modo, setModo] = useState("sin_depositos");
  const [principal, setPrincipal] = useState("");
  const [compras, setCompras] = useState("");
  const [ventas, setVentas] = useState("");
  const [porUsuario, setPorUsuario] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [dForm, setDForm] = useState(null);       // {id?, nombre, codigo, direccion}
  const [transfers, setTransfers] = useState([]);
  const [showTransf, setShowTransf] = useState(false);

  const q = logo ? `?logo_tipo=${logo}` : "";

  const cargarTodos = useCallback(async () => {
    const r = await fetch(`${API}/admin/depositos${q}${q ? "&" : "?"}incluir_inactivos=true`, { headers });
    setTodos(r.ok ? await r.json() : []);
  }, [logo, token]); // eslint-disable-line

  const cargarTransfers = useCallback(async () => {
    const r = await fetch(`${API}/admin/depositos/transferencias/lista${q}`, { headers });
    setTransfers(r.ok ? await r.json() : []);
  }, [logo, token]); // eslint-disable-line

  useEffect(() => { cargarTodos(); cargarTransfers(); }, [cargarTodos, cargarTransfers]);
  useEffect(() => {
    setModo(cfg.modo || "sin_depositos");
    setPrincipal(cfg.deposito_principal_id || "");
    setCompras(cfg.deposito_compras_id || "");
    setVentas(cfg.deposito_ventas_id || "");
    setPorUsuario(cfg.ventas_por_usuario || {});
  }, [cfg]);
  useEffect(() => {
    fetch(`${API}/admin/usuarios`, { headers }).then(r => r.ok ? r.json() : []).then(u => setUsuarios(Array.isArray(u) ? u : [])).catch(() => {});
  }, [token]); // eslint-disable-line

  const activosList = todos.filter(d => d.activo !== false);
  const usuariosEmpresa = usuarios.filter(u => u.activo !== false);

  const guardarConfig = async () => {
    if (modo !== "sin_depositos" && !principal) { toast.error("Elegí el depósito principal"); return; }
    if (modo !== "sin_depositos" && !cfg.activo &&
        !window.confirm("Se va a asignar TODA la mercadería actual al depósito principal. Esta acción se hace una sola vez. ¿Continuar?")) return;
    setGuardando(true);
    try {
      const r = await fetch(`${API}/admin/depositos/config`, {
        method: "PUT", headers,
        body: JSON.stringify({
          logo_tipo: logo, modo, deposito_principal_id: principal || null, deposito_compras_id: compras || null,
          deposito_ventas_id: ventas || null, ventas_por_usuario: porUsuario,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "No se pudo guardar");
      toast.success(d.productos_asignados ? `Configuración guardada. ${d.productos_asignados} productos asignados al depósito principal.` : "Configuración guardada");
      recargar(); cargarTodos();
    } catch (e) { toast.error(e.message); } finally { setGuardando(false); }
  };

  const guardarDeposito = async () => {
    if (!dForm.nombre?.trim()) { toast.error("El nombre es obligatorio"); return; }
    const body = { logo_tipo: logo, nombre: dForm.nombre, codigo: dForm.codigo || null, direccion: dForm.direccion || null, activo: dForm.activo !== false };
    const r = await fetch(`${API}/admin/depositos${dForm.id ? `/${dForm.id}` : ""}`, { method: dForm.id ? "PUT" : "POST", headers, body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(d.detail || "No se pudo guardar"); return; }
    toast.success("Depósito guardado");
    setDForm(null); cargarTodos(); recargar();
  };

  const eliminar = async (d) => {
    if (!window.confirm(`¿Eliminar el depósito "${d.nombre}"?`)) return;
    const r = await fetch(`${API}/admin/depositos/${d.id}${q}`, { method: "DELETE", headers });
    const x = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(x.detail || "No se pudo eliminar"); return; }
    toast.success(x.desactivado ? "Tenía historial: quedó inactivo" : "Depósito eliminado");
    cargarTodos(); recargar();
  };

  const reimprimir = async (t) => {
    const r = await fetch(`${API}/admin/depositos/transferencias/${t.id}`, { headers });
    if (r.ok) imprimirRemitoDeposito(await r.json());
  };
  const anular = async (t) => {
    const motivo = window.prompt(`Motivo de la anulación de ${t.numero}:`);
    if (!motivo) return;
    const r = await fetch(`${API}/admin/depositos/transferencias/${t.id}/anular`, { method: "POST", headers, body: JSON.stringify({ motivo }) });
    const x = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(x.detail || "No se pudo anular"); return; }
    toast.success("Transferencia anulada"); cargarTransfers(); cargarTodos();
  };

  if (cargando && !todos.length) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      {/* Modo */}
      <div className="border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="font-heading text-white text-sm flex items-center gap-2"><Warehouse className="w-4 h-4 text-cyan-400" /> Modo de trabajo</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {MODOS.map(m => (
            <button key={m.v} disabled={!puedeGestionar} onClick={() => setModo(m.v)}
              className={`text-left rounded-xl border p-3 transition-colors ${modo === m.v ? "border-cyan-500/60 bg-cyan-500/10" : "border-white/10 hover:border-white/25"}`}>
              <p className="text-white text-sm font-medium">{m.t}</p>
              <p className="text-slate-500 text-xs mt-1">{m.d}</p>
            </button>
          ))}
        </div>

        {modo !== "sin_depositos" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-slate-400 text-xs mb-1">Depósito principal</label>
              <select disabled={!puedeGestionar} value={principal} onChange={e => setPrincipal(e.target.value)} className={inp}>
                <option value="">Seleccionar...</option>
                {activosList.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
              </select>
              {!cfg.activo && <p className="text-amber-400/80 text-[11px] mt-1">Al activarlo, toda la mercadería actual pasa a este depósito.</p>}
            </div>
            {modo === "por_compra" && (
              <div>
                <label className="block text-slate-400 text-xs mb-1">Depósito predeterminado para compras</label>
                <select disabled={!puedeGestionar} value={compras} onChange={e => setCompras(e.target.value)} className={inp}>
                  <option value="">Igual al principal</option>
                  {activosList.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-slate-400 text-xs mb-1">Depósito de ventas predeterminado</label>
              <select disabled={!puedeGestionar} value={ventas} onChange={e => setVentas(e.target.value)} className={inp}>
                <option value="">Igual al principal</option>
                {activosList.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
              </select>
            </div>
          </div>
        )}

        {modo !== "sin_depositos" && usuariosEmpresa.length > 0 && (
          <div>
            <p className="text-slate-300 text-xs font-medium mb-2">Depósito de ventas por usuario (opcional)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {usuariosEmpresa.map(u => (
                <div key={u.id} className="flex items-center gap-2">
                  <span className="text-xs text-slate-300 w-40 truncate">{u.name}</span>
                  <select disabled={!puedeGestionar} value={porUsuario[u.id] || ""} onChange={e => setPorUsuario(p => ({ ...p, [u.id]: e.target.value }))} className={inp}>
                    <option value="">Predeterminado de ventas</option>
                    {activosList.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {puedeGestionar && (
          <div className="flex justify-end">
            <button onClick={guardarConfig} disabled={guardando} className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-700 flex items-center gap-2">
              {guardando && <Loader2 className="w-4 h-4 animate-spin" />} Guardar configuración
            </button>
          </div>
        )}
      </div>

      {/* Depósitos */}
      <div className="border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-white text-sm">Depósitos</h3>
          {puedeGestionar && <button onClick={() => setDForm({ nombre: "", codigo: "", direccion: "" })} className="px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs hover:bg-white/15 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Nuevo depósito</button>}
        </div>
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-xs"><tr><th className="text-left py-2">Código</th><th className="text-left">Nombre</th><th className="text-left">Dirección</th><th className="text-right">Productos</th><th className="text-right">Unidades</th><th className="text-center">Estado</th><th></th></tr></thead>
          <tbody>
            {todos.map(d => (
              <tr key={d.id} className={`border-t border-white/5 ${d.activo === false ? "opacity-50" : ""}`}>
                <td className="py-2 text-slate-400">{d.codigo}</td>
                <td className="text-white">{d.nombre}{d.es_principal && cfg.activo && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300">Principal</span>}</td>
                <td className="text-slate-400">{d.direccion || "—"}</td>
                <td className="text-right text-slate-300">{d.stock_items}</td>
                <td className="text-right text-white">{fmt(d.stock_unidades)}</td>
                <td className="text-center text-xs text-slate-400">{d.activo === false ? "Inactivo" : "Activo"}</td>
                <td className="text-right whitespace-nowrap">
                  {puedeGestionar && <>
                    <button onClick={() => setDForm({ ...d })} className="p-1.5 text-slate-400 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => eliminar(d)} className="p-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>}
                </td>
              </tr>
            ))}
            {!todos.length && <tr><td colSpan={7} className="py-6 text-center text-slate-500 text-xs">Todavía no hay depósitos. Creá el primero para poder activarlos.</td></tr>}
          </tbody>
        </table>
        <p className="text-slate-500 text-[11px] mt-2">Un depósito con mercadería no se puede eliminar ni desactivar: transferí primero el stock a otro depósito.</p>
      </div>

      {/* Transferencias */}
      {cfg.activo && (
        <div className="border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-white text-sm flex items-center gap-2"><ArrowLeftRight className="w-4 h-4 text-cyan-400" /> Transferencias entre depósitos</h3>
            {puedeTransferir && <button onClick={() => setShowTransf(true)} className="px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-xs hover:bg-cyan-700 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Nueva transferencia</button>}
          </div>
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs"><tr><th className="text-left py-2">N°</th><th className="text-left">Fecha</th><th className="text-left">Origen → Destino</th><th className="text-right">Unidades</th><th className="text-left">Usuario</th><th className="text-center">Estado</th><th></th></tr></thead>
            <tbody>
              {transfers.map(t => (
                <tr key={t.id} className="border-t border-white/5">
                  <td className="py-2 text-white">{t.numero}</td>
                  <td className="text-slate-400">{new Date(t.fecha).toLocaleString("es-PY")}</td>
                  <td className="text-slate-300">{t.origen_nombre} → {t.destino_nombre}</td>
                  <td className="text-right text-white">{fmt(t.total_unidades)}</td>
                  <td className="text-slate-400">{t.usuario_nombre}</td>
                  <td className={`text-center text-xs ${t.estado === "anulada" ? "text-red-400" : "text-emerald-400"}`}>{t.estado === "anulada" ? "Anulada" : "Confirmada"}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => reimprimir(t)} title="Imprimir remito" className="p-1.5 text-slate-400 hover:text-white"><Printer className="w-3.5 h-3.5" /></button>
                    {puedeAnular && t.estado !== "anulada" && <button onClick={() => anular(t)} title="Anular" className="p-1.5 text-red-400 hover:text-red-300"><Ban className="w-3.5 h-3.5" /></button>}
                  </td>
                </tr>
              ))}
              {!transfers.length && <tr><td colSpan={7} className="py-6 text-center text-slate-500 text-xs">Sin transferencias registradas.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {dForm && (
        <div className="fixed inset-0 z-[250] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="font-heading text-white text-base">{dForm.id ? "Editar depósito" : "Nuevo depósito"}</h3>
            <div><label className="block text-slate-400 text-xs mb-1">Nombre</label><input value={dForm.nombre} onChange={e => setDForm(f => ({ ...f, nombre: e.target.value }))} className={inp} /></div>
            <div><label className="block text-slate-400 text-xs mb-1">Código</label><input value={dForm.codigo || ""} onChange={e => setDForm(f => ({ ...f, codigo: e.target.value }))} placeholder="Automático" className={inp} /></div>
            <div><label className="block text-slate-400 text-xs mb-1">Dirección</label><input value={dForm.direccion || ""} onChange={e => setDForm(f => ({ ...f, direccion: e.target.value }))} className={inp} /></div>
            {dForm.id && <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={dForm.activo !== false} onChange={e => setDForm(f => ({ ...f, activo: e.target.checked }))} /> Activo</label>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDForm(null)} className="px-4 py-2 rounded-lg bg-white/10 text-slate-300 text-sm">Cancelar</button>
              <button onClick={guardarDeposito} className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-700">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {showTransf && (
        <TransferenciaDepositoModal token={token} logo={logo} depositos={depositos} onClose={() => setShowTransf(false)}
          onDone={() => { cargarTransfers(); cargarTodos(); }} />
      )}
    </div>
  );
}
